import { useEffect, type MouseEvent } from 'react'
import { AnimatePresence, motion, type Transition, type Variants } from 'framer-motion'
import { Check, Sparkles, X } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button, Cell, Loadstone, LoadingState, thinkingLoadstoneConfig, Tooltip } from '@highlight/ui'
import { SidepanelCellRow } from '../cells/row'
import { useDayZeroTour, useDayZeroTourEnabled, useSettleTour, useStartMission } from './use-day-zero-tour'
import { findMission, trackTour, type TourStop } from './tour-state'

/** Rounds already reported shown this session, keyed by their stops. */
const shownRounds = new Set<string>()

/** Generation takes ~20s, mostly one model call over the user's deep research; these follow its steps. */
const GENERATING_MESSAGES = [
  'Reading what Highlight learned about your work…',
  "Picking the parts of Highlight you'll use most…",
  'Writing your tour…',
]
const GENERATING_INTERVAL_MS = 4000

const FINALE_VARIANTS: Variants = {
  hidden: { opacity: 0, y: 12, filter: 'blur(4px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)' },
}
const FINALE_TRANSITION: Transition = { type: 'spring', bounce: 0, duration: 0.4 }

/** Framed like the meeting tour's offer card. */
export function DayZeroTour({ className }: { className?: string }) {
  // The tour's hooks read feeds and calendars, so they run only for users in the experiment's test arm.
  return useDayZeroTourEnabled() ? <DayZeroTourList className={className} /> : null
}

function DayZeroTourList({ className }: { className?: string }) {
  const { stops, visited, isFollowUp, isGenerating } = useDayZeroTour()
  const { finishRound, dismissRound } = useSettleTour()
  const startMission = useStartMission()

  const roundKey = stops.map((stop) => stop.mission).join(',')
  useEffect(() => {
    if (isGenerating || !roundKey || shownRounds.has(roundKey)) {
      return
    }
    shownRounds.add(roundKey)
    trackTour('shown', { stops: roundKey.split(',').length, round: isFollowUp ? 'follow-up' : 'first' })
  }, [roundKey, isGenerating, isFollowUp])

  if (!isGenerating && stops.length === 0) {
    return null
  }

  const allDone = stops.length > 0 && stops.every((stop) => visited.includes(stop.mission))

  return (
    <section
      className={twMerge(
        'bg-background corner-smooth relative mx-1 mb-3 overflow-hidden rounded-2xl border pb-1',
        className,
      )}
    >
      <header className="relative flex items-center gap-2.5 px-3 pb-1 pt-3">
        <Sparkles aria-hidden="true" className="text-foreground-secondary size-4 shrink-0" />
        <p className="text-foreground flex-1 text-sm font-medium">
          {isFollowUp ? 'New in Highlight' : 'Take the tour'}
        </p>
        {stops.length > 0 && (
          <span className="text-foreground-placeholder text-xs font-medium tabular-nums">
            {visited.length} of {stops.length}
          </span>
        )}
        {!isGenerating && !allDone && (
          <Tooltip>
            <Tooltip.Trigger
              render={
                <Button
                  variant="ghost"
                  size="xs"
                  shape="square"
                  aria-label="Dismiss tour"
                  className="-my-1 -mr-1"
                  onClick={dismissRound}
                >
                  <X />
                </Button>
              }
            />
            <Tooltip.Content>Dismiss tour</Tooltip.Content>
          </Tooltip>
        )}
      </header>
      {isGenerating ? (
        <LoadingState className="relative px-3 py-2 text-sm">
          <Loadstone config={thinkingLoadstoneConfig} size={14} />
          <LoadingState.Text messages={GENERATING_MESSAGES} interval={GENERATING_INTERVAL_MS} shimmer />
        </LoadingState>
      ) : (
        <div className="relative">
          {stops.map((stop) =>
            visited.includes(stop.mission) ? (
              <DoneRow key={stop.mission} stop={stop} />
            ) : (
              <TourStopRow key={stop.mission} stop={stop} onStart={startMission} />
            ),
          )}
          <AnimatePresence>{allDone && <TourFinale onDone={finishRound} />}</AnimatePresence>
        </div>
      )}
    </section>
  )
}

function DoneRow({ stop }: { stop: TourStop }) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-1.5">
      <div className="bg-element-active text-foreground-placeholder flex size-5 shrink-0 items-center justify-center rounded-full">
        <Check className="size-3" strokeWidth={3} />
      </div>
      <p className="text-foreground-placeholder decoration-foreground-disabled truncate text-sm line-through">
        {stop.title}
      </p>
    </div>
  )
}

function TourFinale({ onDone }: { onDone: () => void }) {
  return (
    <motion.div
      className="flex flex-col gap-3 px-3 pb-2 pt-2"
      variants={FINALE_VARIANTS}
      initial="hidden"
      animate="visible"
      exit="hidden"
      transition={FINALE_TRANSITION}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="text-foreground text-sm font-medium">Tour complete</p>
        <p className="text-foreground-secondary text-sm">You know your way around Highlight.</p>
      </div>
      <div className="flex items-center justify-end">
        <Button variant="primary" onClick={onDone}>
          Done
        </Button>
      </div>
    </motion.div>
  )
}

function TourStopRow({ stop, onStart }: { stop: TourStop; onStart: (stop: TourStop) => void }) {
  const Icon = findMission(stop.mission)?.icon ?? Sparkles
  const start = () => onStart(stop)
  const startFromPill = (event: MouseEvent) => {
    event.stopPropagation()
    start()
  }

  return (
    <SidepanelCellRow
      icon={
        <Cell.Icon className="self-center">
          <Icon className="text-foreground-secondary" />
        </Cell.Icon>
      }
      title={stop.title}
      subtitle={<span className="text-foreground-secondary type-ui-xs line-clamp-2">{stop.reason}</span>}
      align="center"
      onClick={start}
      trailing={
        <Button size="sm" variant="outline" className="shrink-0" onClick={startFromPill}>
          Show me
        </Button>
      }
    />
  )
}
